import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthGuard } from './components/AuthGuard';
import { ConfigProvider } from './context/ConfigContext';
import PosWelcomePage from './components/PosWelcomePage';
import PosAdminPage from './components/PosAdminPage';
import PosPage from './components/PosPage';
import PosSubscribeCheckout from './components/PosSubscribeCheckout';
import PosLandingPayment from './components/PosLandingPayment';
import { CHECKOUT_PATH_SEGMENT, CHECKOUT_SIGNUP_ROUTE_PATH } from './constants/bookingConst';
import './css/global.css';

/**
 * One client's till, with its config provider wired to the URL.
 *
 * The provider is mounted HERE rather than around the whole app because the
 * client is a property of the route: / and /admin belong to no client,
 * and a provider above the router could not read the parameter at all. Keying
 * it on clientId also means moving between two clients tears the old
 * subscriptions down and opens new ones, rather than leaving one client's
 * products on screen under another's name.
 *
 * @returns {JSX.Element}
 */
function PosClientRoute() {
  const { clientId } = useParams();
  return (
    <ConfigProvider key={clientId} clientId={clientId}>
      <PosPage />
    </ConfigProvider>
  );
}

/**
 * A till's subscription checkout tab, with that client's config provider —
 * so its currency and theme load with it, the same as the till itself.
 *
 * @returns {JSX.Element}
 */
function PosClientCheckoutRoute() {
  const { clientId } = useParams();
  return (
    <ConfigProvider key={clientId} clientId={clientId}>
      <PosSubscribeCheckout />
    </ConfigProvider>
  );
}

/**
 * The Point of Sale app.
 *
 *   /              public welcome page — no client, no sign-in
 *   /admin         the estate: clients, registration, global config
 *   /<client-id>   one client's till
 *   /<client-id>/checkout/<session>   that till's subscription checkout tab
 *   /checkout/<session>               the landing page's sign-up checkout tab
 *
 * ROUTE ORDER MATTERS LESS THAN IT LOOKS: React Router ranks a static segment
 * above a dynamic one, so /admin wins over /:clientId however the two
 * are written here. "admin" is ALSO in POS_RESERVED_CLIENT_IDS, so a client
 * can never be registered under an id that would sit in its shadow — belt and
 * braces, because the two protections fail in different ways.
 *
 * AuthGuard sits above everything: the database rules gate every read on
 * `auth != null`, and even the welcome page's provider reads global config.
 *
 * @returns {JSX.Element}
 */
function App() {
  return (
    <AuthGuard>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={(
              <ConfigProvider>
                <PosWelcomePage />
              </ConfigProvider>
            )}
          />
          <Route
            path="/admin"
            element={(
              <ConfigProvider>
                <PosAdminPage />
              </ConfigProvider>
            )}
          />
          <Route path="/:clientId" element={<PosClientRoute />} />
          {/* Checkout tabs, opened by hooks/useCheckoutTab. Two and three
              segments long, so neither can be mistaken for a client's till. */}
          <Route path={`/:clientId/${CHECKOUT_PATH_SEGMENT}/:sessionId`} element={<PosClientCheckoutRoute />} />
          <Route
            path={`${CHECKOUT_SIGNUP_ROUTE_PATH}/:sessionId`}
            element={(
              <ConfigProvider>
                <PosLandingPayment />
              </ConfigProvider>
            )}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthGuard>
  );
}

export default App;
